import { withNamespaces } from "react-i18next";

import DateUtils from "src/services/utils/DateUtils";

import { INTERNATIONAL_DATE_FORMAT } from "src/common/constants";

import ReactApexChart from "react-apexcharts";

import { useQuery } from "@tanstack/react-query";

import { toast } from "react-toastify";

import {
  AvatarGroup,
  AvatarItem,
} from "src/modules/data-protection/components/avatar-group";

import DataProtectionSoftwareService from "src/modules/data-protection/apis/DataProtectionSoftwareService";

import React, { useImperativeHandle } from "react";

import { Col, Row, UncontrolledTooltip } from "reactstrap";

import OverviewHeaderContent from "src/modules/data-protection/components/overview-header";
import StatusBadge from "src/modules/data-protection/components/StatusBadge";

import { createSVGFromText } from "src/modules/data-protection/helpers/common";

import {
  HiChevronDoubleDown,
  HiChevronDoubleUp,
  HiMenuAlt4,
} from "react-icons/hi";

import { Assets_STATUS_LIST, AIRisks, PROVIDER_CONFORMITY_STATUS } from "../../../../constants"
import { RiInformationLine } from "react-icons/ri";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";

const PrivacyRisks = {
    'high': {
        color: '#DC3545',
        title: 'High',
    },
    'medium' : {
        color: '#FCC34B',
        title: 'Medium',
    },
    'low' : {
        color: '#1CBB8C',
        title: 'Low'
    }
};

const OverviewHeader = ({ t, assetId, actionsRef }) => {

  const dateUtils = new DateUtils();

  const {
    data: asset,
    refetch: refetchHeaderDetail,
    isFetching: headerDetailIsFetching
  } = useQuery({
    queryKey: ["data-protection-fetch-overview-content"],
    queryFn: async () => {
      const service = DataProtectionSoftwareService.getInstance();
      return await service.details(assetId);
    },
    cacheTime: 0,
    refetchOnWindowFocus: false,
    onError: (error) => {
      toast(t("An error occurred while fetching overview content."), {
        type: "error",
      });
    },
  });

  const renderLoadingSection = () => {
    return (
      <Row>
        <Col sm="12">
          <div className="loading-section p-5">
            <Row className="mb-5">
              <Col sm="12" md="3">
                <div
                  className="dt-field dt-skeleton mb-1"
                  style={{ width: "40%" }}
                ></div>
                <div
                  className="dt-field dt-skeleton"
                  style={{ width: "70%" }}
                ></div>
              </Col>
              <Col sm="12" md="3">
                <div
                  className="dt-field dt-skeleton mb-1"
                  style={{ width: "40%" }}
                ></div>
                <div
                  className="dt-field dt-skeleton"
                  style={{ width: "70%" }}
                ></div>
              </Col>
              <Col sm="12" md="3">
                <div
                  className="dt-field dt-skeleton mb-1"
                  style={{ width: "40%" }}
                ></div>
                <div
                  className="dt-field dt-skeleton"
                  style={{ width: "70%" }}
                ></div>
              </Col>
              <Col sm="12" md="3">
                <div
                  className="dt-field dt-skeleton mb-1"
                  style={{ width: "40%" }}
                ></div>
                <div
                  className="dt-field dt-skeleton"
                  style={{ width: "70%" }}
                ></div>
              </Col>
            </Row>
            <Row>
              <Col sm="12" md="4">
                <div
                  className="dt-field dt-skeleton mb-1"
                  style={{ width: "100%" }}
                ></div>
              </Col>
              <Col sm="12" md="4">
                <div
                  className="dt-field dt-skeleton mb-1"
                  style={{ width: "100%" }}
                ></div>
              </Col>
              <Col sm="12" md="4">
                <div
                  className="dt-field dt-skeleton mb-1"
                  style={{ width: "100%" }}
                ></div>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    );
  };

  const renderAssignedAnalysts = () => {
    const analysts = asset?.analyst_assigned_object;

    if (!analysts || Array.isArray(analysts)) {
      return "---";
    }

    const displayName =
      analysts?.name ||
      `${analysts?.first_name ?? ""} ${analysts?.last_name ?? ""}`.trim();

    if (!displayName) {
      return "---";
    }

    if (analysts?.avatar && analysts.avatar !== "") {
      return (
        <div className="d-flex justify-content-center align-items-center flex-column">
          <div className="avatar-xs">
            <span
              className="avatar-title bg-soft-primary text-primary rounded-circle text-capitalize"
              style={{ overflow: "hidden" }}
            >
              <img
                alt={displayName}
                src={analysts?.avatar}
                style={{
                  width: "40px",
                  height: "40px",
                  display: "block",
                  objectFit: "cover",
                }}
              />
            </span>
          </div>
          <p className="fs-6">{displayName}</p>
        </div>
      );
    }

    const initials = displayName
      .split(" ")
      .map((part) => part.charAt(0))
      .join("");

    const xml = new XMLSerializer().serializeToString(
      createSVGFromText(initials)
    );

    const result = [
      <AvatarItem
        key="overview-analyst-avatar"
        id="overview-analyst-avatar"
        title={displayName}
        src={`data:image/svg+xml;charset=utf-8,${xml}`}
        alt=""
      />,
    ];

    return <AvatarGroup max={4}>{result}</AvatarGroup>;
  };

  const handleRenderStatus = (status) => {
    const statusData = Assets_STATUS_LIST[status?.toUpperCase()];

    if (!statusData) {
      return "---";
    }

    return (
      <StatusBadge
        label={t(statusData.title)}
        backgroundColor={statusData.backgroundColor}
        opacity={statusData.opacity || 1}
      />
    );
  };

  useImperativeHandle(actionsRef, () => {
    return {
        reload  :   () => {
            refetchHeaderDetail();
        }
    }
});

    const secondRow = [
        {
            title: "Assigned Analyst",
            value: renderAssignedAnalysts(),
        },
        {
            title: "Mitigated Risks",
            value: (
                asset?.completed_mitigated_tasks ?
                    <>
                        <ReactApexChart
                            options={{
                                chart: {
                                    sparkline: {
                                        enabled: true,
                                    },
                                },
                                dataLabels: {
                                    enabled: false,
                                },
                                colors: ["#FF4949"],
                                stroke: {
                                    lineCap: "round",
                                },
                                plotOptions: {
                                    radialBar: {
                                        hollow: {
                                            margin: 0,
                                            size: "70%",
                                        },
                                        track: {
                                            margin: 0,
                                        },

                                        dataLabels: {
                                            show: false,
                                        },
                                    },
                                },
                            }}
                            series={[asset?.completed_mitigated_tasks]}
                            type="radialBar"
                            height="60"
                        />
                        <p className="text-center">
                            {`${asset?.completed_mitigated_tasks}%`}
                        </p>
                    </>
                    :
                    <p>{t("No mitigated risk")}</p>
            ),
        },
        {
            title: "Completed Tasks",
            value: (
                asset?.completed_tasks ?
                    <>
                        <ReactApexChart
                            options={{
                                chart: {
                                    sparkline: {
                                        enabled: true,
                                    },
                                },
                                dataLabels: {
                                    enabled: false,
                                },
                                colors: ["#195A4D"],
                                stroke: {
                                    lineCap: "round",
                                },
                                plotOptions: {
                                    radialBar: {
                                        hollow: {
                                            margin: 0,
                                            size: "70%",
                                        },
                                        track: {
                                            margin: 0,
                                        },

                                        dataLabels: {
                                            show: false,
                                        },
                                    },
                                },
                            }}
                            series={[asset?.completed_tasks]}
                            type="radialBar"
                            height="60"
                        />
                        <p className="text-center">{asset?.completed_tasks}%</p>
                    </>
                    :
                    <p>{t("No completed task")}</p>
            ),
        },
        {
            title: "Created On",
            value: (
                <>
                    {dateUtils.convertTimeStampToDate(
                        asset?.created_at,
                        INTERNATIONAL_DATE_FORMAT
                    )}
                </>
            ),
        },
        {
            title: "Last Update",
            value: (
                <>
                    {dateUtils.convertTimeStampToDate(
                        asset?.updated_at,
                        INTERNATIONAL_DATE_FORMAT
                    )}
                </>
            ),
        }
    ];

    if(Number(asset?.answer_completion_percent) >= 100){
        secondRow.push({
            title: "Privacy Risks",
            value: (
                <>
                    <span
                        style={{
                            display: "inline-block",
                            textAlign: "center",
                            backgroundColor: PrivacyRisks[asset?.privacy_risk?.toLowerCase()]?.color,
                            minWidth: "120px",
                            borderRadius: "8px",
                            padding: '4px 12px',
                            color: '#1D1D1D'
                        }}
                    >
                        {t(PrivacyRisks[asset?.privacy_risk?.toLowerCase()]?.title) || ""}
                    </span>
                </>
            )
        })
    }

    if (asset?.ai_risk) {
        secondRow.push({
        title: "AI Risk",
        value: (
            <>
                <span
                    style={{
                        display: "inline-block",
                        textAlign: "center",
                        backgroundColor: AIRisks[asset.ai_risk]?.color,
                        borderRadius: '8px',
                        padding: "4px 12px",
                        color: '#1D1D1D',
                        minWidth: "120px"
                    }}
                >
                    {t(AIRisks[asset.ai_risk]?.title)}{' '}
                    <RiInformationLine
                        size="14"
                        id="ai-risk-reason"
                        style={{ cursor: 'pointer' }}
                    />

                    <UncontrolledTooltip
                        target="ai-risk-reason"
                    >
                        {t(AIRisks[asset.ai_risk]?.description || asset.ai_risk_reason)}
                    </UncontrolledTooltip>
                </span>
            </>
        ),
    })
    }

    if (
        isFeatureEnabledForActiveModule("ce-conformity") &&
        asset?.provider_conformity_required
    ) {
        const conformityStatus = PROVIDER_CONFORMITY_STATUS[asset.provider_conformity_status] || {
            color: "secondary",
            label: asset.provider_conformity_status || "Unknown"
        };

        secondRow.push({
            title: "CE Conformity",
            value: (
                <StatusBadge
                    label={t(conformityStatus.label)}
                    color={conformityStatus.color}
                />
            )
        });
    }

    secondRow.push({
        title: "Financial Operational Risk",
        value: (
            <>
                <span
                    style={{
                        display: "inline-block",
                        textAlign: "center",
                        backgroundColor: "#" + asset?.financial_risk?.color,
                        borderRadius: '8px',
                        padding: "4px 12px",
                        color: '#1D1D1D',
                        minWidth: "120px"
                    }}
                >
                    {asset?.financial_risk?.name
                        ? t(asset?.financial_risk?.name)
                        : ""}
                </span>
            </>
        ),
    })

    return (
        <React.Fragment>
        {headerDetailIsFetching ||
        !asset ? (
            <>{renderLoadingSection()}</>
        ) : (
            <OverviewHeaderContent
            items={[
                [
                {
                    title: t("Asset ID"),
                    value: asset?.secondary_id,
                },
                {
                    title: t("Service"),
                    value: asset?.service,
                },
                {
                    title: t("Version"),
                    value: asset?.version,
                },
                {
                    title: t("Operating System"),
                    value: t(asset?.operating_system?.full_name),
                },
                {
                    title: t("Service Provider"),
                    value: t(asset?.provider_name),
                },
                {
                    title: t("DPIA"),
                    value: (
                    <>
                        <span style={{ display: "flex", justifyContent: "center" }}>
                        {asset?.DPIA_status === "NOT_NEEDED" ? t("Not needed") : t("Needs")}
                        </span>{" "}
                        {
                            asset?.DPIA_status === "NEEDED_AND_COMPLETED" ? <span style={{ display: "flex" }} className="text-success">
                            { t("Completed") }
                            </span> : asset?.DPIA_status === "NEEDED_AND_NOT_FILLED" ? <span style={{ display: "flex" }} className="text-danger">
                                { t("Has not been filled yet") }
                            </span> : ""}
                    </>
                    ),
                },
                {
                    title: t("DPA"),
                    value: <span>{asset?.DPA_status ? t("Yes") : t("No")}</span>,
                },
                {
                    title: t("Status"),
                    value: (
                    <>
                        {asset?.status ? handleRenderStatus(asset.status) : ""}
                    </>
                    ),
                },
                {
                    title: t("Priority"),
                    value: (
                    <div
                        style={{ display: "flex", alignItems: "center", gap: "5%" }}
                    >
                        {asset?.priority.toLowerCase() === "low" ? (
                        <HiChevronDoubleDown style={{ color: "#1CBB8C" }} />
                        ) : asset?.priority.toLowerCase() === "medium" ? (
                        <HiMenuAlt4 style={{ color: "#FCC34B" }} />
                        ) : (
                        <HiChevronDoubleUp style={{ color: "#DC354" }} />
                        )}
                        <span>
                        {t(
                            asset?.priority.charAt(0).toUpperCase() +
                            asset?.priority.slice(1).toLowerCase()
                        )}
                        </span>
                    </div>
                    ),
                },
                ],
                secondRow,
            ]}
            />
        )}
        </React.Fragment>
    );
};

export default withNamespaces()(OverviewHeader);
